import type { AreaCoverageVO, CityRegion, RouterIdsVO } from "@/api"
import { getCityRegion, getShenzhenData } from "@/api"
import { deepClone, isInvalid } from "@/util/util"
import { CaretDownOutlined } from "@ant-design/icons"
import { useRequest, useSetState } from "ahooks-v2"
import { Button, Col, Radio, Row } from "antd"
import type { ForwardRefRenderFunction } from "react"
import { forwardRef, useCallback, useEffect, useImperativeHandle } from "react"
import { useParams } from "react-router-dom"
import "./index.scss"
// ==============================================
import Popover from "@/components/Popover"
// ==============================================
const RadioGroup = Radio.Group
const RadioButton = Radio.Button
const entireDistrict = {
  name: "全部区县",
  id: "",
}
const entireStreet = {
  name: "全部街道",
  id: "",
}
// ==============================================
interface stateVO {
  visible: boolean
  // 区县
  districtList: CityRegion[]
  districtId: string
  districtName: string
  // 街道
  streetList: CityRegion[]
  streetId: string
  streetName: string
}
interface AreaCoverageProps {
  tabKey: string
  data: AreaCoverageVO
  // 是否请求数据
  isRequest?: boolean
  // 传递数据到父组件
  passData?: (key: string, data: AreaCoverageVO) => void
  // 弹出框visible变化
  onDropdownVisibleChange?: (visible: boolean) => void
}
const AreaCoverage: ForwardRefRenderFunction<any, AreaCoverageProps> = (
  { tabKey, data, isRequest = true, passData, onDropdownVisibleChange },
  ref: any,
) => {
  const { cityId, sandTableId } = useParams<RouterIdsVO>() as any
  const [state, set] = useSetState<stateVO>({
    ...data,
    visible: false,
    districtList: [],
    streetList: [],
  })
  const { visible, districtList, streetList, districtId, streetId } = state

  // 处理城市数据
  const processCityDataFn = useCallback(
    (cityData) => {
      const districtList = cityData?.[0]?.children?.slice() || []
      let streetList = []
      // 所有street
      const streetAllList: CityRegion[] = []
      for (const item of districtList) {
        if (item?.children) {
          streetAllList.push(...item.children)
        }
      }
      // 插入全部区县
      const cloneEntireDistrict = deepClone(entireDistrict)
      cloneEntireDistrict.children = streetAllList.slice()
      districtList.unshift(cloneEntireDistrict)
      // 插入全部街道
      streetList = streetAllList.slice()
      const cloneEntireStreet = deepClone(entireStreet)
      streetList.unshift(cloneEntireStreet)
      set({
        districtList,
        streetList,
        districtId: "",
        streetId: "",
        districtName: "全部区县",
        streetName: "全部街道",
      })
    },
    [set],
  )

  // 获取深圳静态数据
  const { run: shenzhenRun } = useRequest(getShenzhenData, {
    manual: true,
    onSuccess: (res) => {
      // 处理城市数据
      processCityDataFn(res)
    },
    onError: () => {
      set({
        districtList: [],
        streetList: [],
      })
    },
  })

  // 区县街道
  const { run: cityRun } = useRequest(
    () =>
      getCityRegion({
        cityId,
        sandTableId,
      }),
    {
      manual: true,
      onSuccess: (res) => {
        // 处理城市数据
        processCityDataFn(res)
      },
      onError: () => {
        set({
          districtList: [],
          streetList: [],
        })
      },
    },
  )

  // 监听数据变化
  useEffect(() => {
    if (isRequest) {
      // 区县街道
      cityRun()
    } else {
      // 获取深圳静态数据
      shenzhenRun()
    }
  }, [isRequest, cityId, sandTableId, shenzhenRun, cityRun])

  // 区县change
  const districtChange = useCallback(
    (value: string, streetId?: string) => {
      set((prev) => {
        let districtName = ""
        let streetName = ""
        let streetList: CityRegion[] = []
        const districtList = (prev.districtList || []).slice()
        for (const item of districtList) {
          if (item.id === value) {
            districtName = item.name
            // 插入全部街道
            const cloneEntireStreet = deepClone(entireStreet)
            streetList = (item?.children || []).slice()
            streetList.unshift(cloneEntireStreet)
            break
          }
        }
        if (!isInvalid(streetId)) {
          for (const item of streetList) {
            if (item.id === streetId) {
              streetName = item.name
              break
            }
          }
        } else {
          // eslint-disable-next-line no-param-reassign
          streetId = ""
          streetName = "全部街道"
        }
        return {
          streetList,
          districtId: value,
          districtName,
          streetId,
          streetName,
        }
      })
    },
    [set],
  )

  // 街道change
  const streetChange = useCallback(
    (event) => {
      const value = event.target.value
      set((prev) => {
        const streetName = prev.streetList?.filter((item) => item.id === value)?.[0]?.name || ""
        return {
          streetId: value,
          streetName,
        }
      })
    },
    [set],
  )

  // Popover开关
  const togglePopover = useCallback(
    (visible: boolean) => {
      set({
        visible,
      })
      // 弹出框visible变化
      onDropdownVisibleChange && onDropdownVisibleChange(visible)
    },
    [set, onDropdownVisibleChange],
  )

  // 确定
  const submitClick = useCallback(() => {
    const { districtId, streetId, districtName, streetName } = state
    // 传递数据到父组件
    passData &&
      passData(tabKey, {
        districtId,
        streetId,
        districtName,
        streetName,
      })
    // Popover开关
    togglePopover(false)
  }, [tabKey, state, passData, togglePopover])

  // 重置为默认数据
  const resetClick = useCallback(() => {
    // 区县change
    districtChange("", "")
  }, [districtChange])

  // 重置为上一次的数据
  const resetBeforeData = useCallback(() => {
    const { districtId, streetId } = data
    // 区县change
    districtChange(districtId, streetId)
  }, [data, districtChange])

  // 打开弹窗时，重置为上一次数据
  useEffect(() => {
    if (visible) {
      // 重置为上一次的数据
      resetBeforeData()
    }
  }, [visible, resetBeforeData])

  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    togglePopover,
  }))

  return (
    <Popover
      key={`${tabKey}Popover`}
      className="area-coverage-popover"
      visible={visible}
      content={
        <>
          <div className="district-street-box">
            <Row key="district" className="row-district">
              <Col span={3} className="word-nowrap district-street-title">
                区县：
              </Col>
              <Col span={21}>
                <RadioGroup
                  className="theme-radio-group"
                  buttonStyle="solid"
                  value={districtId}
                  onChange={(event) => districtChange(event.target.value)}
                >
                  {districtList?.map((item) => (
                    <RadioButton value={item.id} key={item.id}>
                      {item.name}
                    </RadioButton>
                  ))}
                </RadioGroup>
              </Col>
            </Row>
            <Row key="street">
              <Col span={3} className="word-nowrap district-street-title">
                街道：
              </Col>
              <Col span={21}>
                <RadioGroup
                  className="theme-radio-group"
                  buttonStyle="solid"
                  value={streetId}
                  onChange={streetChange}
                >
                  {streetList?.map((item) => (
                    <RadioButton value={item.id} key={item.id}>
                      {item.name}
                    </RadioButton>
                  ))}
                </RadioGroup>
              </Col>
            </Row>
          </div>
          <div className="btns">
            <Button className="theme-text-btn" type="text" onClick={() => resetClick()}>
              重置
            </Button>
            <div>
              <Button className="theme-cancel-btn" onClick={() => togglePopover(false)}>
                取消
              </Button>
              &nbsp;
              <Button className="theme-primary-btn" type="primary" onClick={submitClick}>
                确定
              </Button>
            </div>
          </div>
        </>
      }
    >
      <Button
        type="primary"
        className="theme-filter-condition-btn"
        onClick={() => togglePopover(true)}
      >
        {`${data?.districtName} / ${data?.streetName}`}
        <CaretDownOutlined style={{ fontSize: "1rem" }} />
      </Button>
    </Popover>
  )
}

export default forwardRef(AreaCoverage)
